import {
  PaymentTiming,
  TourInstallmentForm,
  TourInstallmentInputForm,
} from '@/features/dashboard/tourConfig/schemas/tourInstallmentsSchema.ts';
import {
  TourInstallmentDto,
  TourInstallmentConfigurationDto,
  ConfigureInstallmentTemplatesRequest,
} from '@/features/dashboard/tourConfig/types/TourInstallmentDto.ts';
import { PaymentMethod } from '@/features/dashboard/tourConfig/types/PaymentMethod.ts';
import { PriceRangeDto } from '@/features/shared/pricing/types/PriceRangeDto.ts';
import { BankAccountDto } from '@/features/dashboard/paymentConfig/types/BankAccountDto.ts';

export const tourInstallmentDtoMapper = {
  fromFormToRequest: (
    form: TourInstallmentForm,
  ): ConfigureInstallmentTemplatesRequest => {
    const installments: TourInstallmentDto[] = [];

    if (form.installments.length === 0) {
      return { installments, bankAccountIds: [] };
    }

    if (form.allowInstallments) {
      form.installments.forEach((installment, index) => {
        const isFirst = index === 0;

        let dueDaysBeforeStart: number | undefined;
        let dueDaysAfterBooking: number | undefined;

        if (isFirst) {
          if (installment.paymentTiming === PaymentTiming.AFTER_RESERVATION) {
            dueDaysAfterBooking = installment.dueDaysAfterReservation as
              | number
              | undefined;
          }
        } else {
          dueDaysBeforeStart = installment.dueDaysBeforeStart as
            | number
            | undefined;
        }

        installments.push({
          paymentMethods: installment.paymentMethods,
          dueDaysBeforeStart,
          dueDaysAfterBooking,
          basePricePercentage: [
            {
              percentage: installment.basePricePercentage,
              currency: undefined, //we dont use per currency price percentage, its prepared for future use
            },
          ],
        });
      });
    } else {
      installments.push({
        paymentMethods: form.installments[0].paymentMethods,
        dueDaysBeforeStart: undefined,
        dueDaysAfterBooking: undefined,
        basePricePercentage: [
          {
            percentage: 100,
            currency: undefined, //we dont use per currency price percentage, its prepared for future use,
          },
        ],
      });
    }

    // first-type installment (no dueDaysBeforeStart, may have dueDaysAfterBooking) stays first;
    // remaining sort DESC by dueDaysBeforeStart
    installments.sort((a, b) => {
      const aIsFirstType = a.dueDaysBeforeStart === undefined;
      const bIsFirstType = b.dueDaysBeforeStart === undefined;
      if (aIsFirstType !== bIsFirstType) return aIsFirstType ? -1 : 1;
      return (b.dueDaysBeforeStart ?? 0) - (a.dueDaysBeforeStart ?? 0);
    });

    const hasAnyTransfer = form.installments.some((inst) =>
      inst.paymentMethods.includes(PaymentMethod.TRANSFER),
    );

    const bankAccountIds = hasAnyTransfer
      ? (form.bankAccountSelections ?? [])
          .map((s) => s.bankAccountId)
          .filter(Boolean)
      : [];

    return { installments, bankAccountIds };
  },
  fromDtoToForm: (
    dto: TourInstallmentConfigurationDto,
    priceRanges?: PriceRangeDto[],
    allBankAccounts?: BankAccountDto[],
  ): TourInstallmentInputForm => {
    const savedSelections = dto.bankAccounts.map((ba) => ({
      currency: ba.currency,
      bankAccountId: ba.id,
    }));

    const bankAccountSelections =
      priceRanges && priceRanges.length > 0
        ? priceRanges.map((range) => {
            const currency = range.min.currency;
            const existing = savedSelections.find(
              (s) => s.currency === currency,
            );
            if (existing) return existing;
            const defaultAccount = allBankAccounts?.find(
              (ba) => ba.currency === currency && ba.isDefault,
            );
            return { currency, bankAccountId: defaultAccount?.id ?? '' };
          })
        : savedSelections;

    const allowInstallments = dto.installments.length > 1;

    const form: TourInstallmentInputForm = {
      allowInstallments,
      installments: [],
      bankAccountSelections,
    };

    dto.installments.forEach((installment, index) => {
      const isFirstOfMultiple = allowInstallments && index === 0;
      const paymentTiming = isFirstOfMultiple
        ? installment.dueDaysAfterBooking != null
          ? PaymentTiming.AFTER_RESERVATION
          : PaymentTiming.AT_RESERVATION
        : undefined;

      form.installments.push({
        paymentMethods: installment.paymentMethods,
        paymentTiming,
        dueDaysBeforeStart: installment.dueDaysBeforeStart,
        dueDaysAfterReservation: installment.dueDaysAfterBooking,
        basePricePercentage:
          installment.basePricePercentage.length > 0
            ? installment.basePricePercentage[0].percentage
            : undefined,
      });
    });

    return form;
  },
};
